"use client"

import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Pure-CSS Card — previously wrapped in `motion.div` with an `initial opacity:0,
 * y:20 → animate` mount animation and `whileHover y:-2`. The mount animation
 * delayed visible content (and thus LCP on pages that render Card as the
 * primary content), and the hover lift dragged framer-motion (~50 kB) into
 * every page that used a Card.
 *
 * The same visual feel is achieved with Tailwind / CSS:
 *   - Mount fade-in via `tw-animate-css`' `animate-in fade-in-0 slide-in-from-bottom-1`.
 *   - Hover lift via `hover:-translate-y-0.5` (~2 px), GPU-accelerated.
 *   - Honors `prefers-reduced-motion` through `motion-safe:`.
 *
 * Behaviour is unchanged for callers; the omitted drag handlers from the
 * previous typing are no longer needed because the underlying element is a
 * plain `<div>`.
 */
function Card({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm",
        "motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:-translate-y-0.5",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-4", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
